<template>
  <b-modal
    id="debitos-mit"
    :ref="'debitos-mit'"
    :title="'Débitos da Apuração MIT' + (empresaNome ? ' - ' + empresaNome : '')"
    no-close-on-backdrop
    no-stacking
    scrollable
    size="lg"
    :dialog-class="'debitos-modal-custom'"
    @hidden="fecharModal"
  >
    <menu-lateral
      :grupos="gruposExistentes"
      :selecionado="grupoSelecionado"
      @selecionado="selecionarGrupo"
    >
      <div
        v-if="carregando"
        class="d-flex align-items-center justify-content-center p-2 text-muted"
      >
        <b-spinner
          small
          class="mr-1"
        />
        Carregando...
      </div>
      <visualizacao
        v-else-if="grupoSelecionado"
        :grupo-debito="grupoSelecionado"
        :itens="listaDebitosSelecionada"
        :periodo-apuracao="periodoApuracaoSelecionado"
        :modo-historico="modoHistorico"
      />
      <div
        v-else
        class="p-1"
      >
        <h5 class="mb-0 text-center"> Sem débitos para o período selecionado </h5>
      </div>
    </menu-lateral>

    <template v-slot:modal-footer>
      <div class="w-100">
        <b-button
          variant="primary"
          size="sm"
          class="float-right"
          @click="fecharModal"
        >
          Fechar
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<style lang="scss">
@import '../../styles/debitos.scss';
</style>

<script>
import service from '@app/mit-apuracao/services/mitApuracaoService'
import grupos from '@app/mit-apuracao/config/gruposDebitos'

export default {
  name: 'DebitosMitApuracao',
  components: {
    MenuLateral: () => import('../debitos/MenuLateral.vue'),
    Visualizacao: () => import('../debitos/Visualizacao.vue'),
  },
  props: {
    empresaNome: { type: String, default: '' },
    empresaId: { type: Number, required: true },
    idApuracao: { type: Number, required: true },
    isFiltroDataAplicada: { type: Boolean, default: false },
    filtroAtual: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      grupoSelecionado: '',
      dados: null,
      payloadEntry: null,
      carregando: false,
      gruposExistentes: grupos,
      pagina: 1,
      tamanhoPagina: 1000, // Tamanho da página para buscar todos os registros
    }
  },
  computed: {
    listaDebitosSelecionada() {
      if (!this.payloadEntry || !this.grupoSelecionado) return []
      // Encontra o mapeamento do grupo selecionado para a chave do backend
      const entry = this.gruposExistentes.find(g => g.label === this.grupoSelecionado || g.key === this.grupoSelecionado)
      const key = entry ? entry.key : null

      const getContainer = (obj, k) => {
        if (!k || !obj) return null
        const lowerFirst = k.charAt(0).toLowerCase() + k.slice(1)
        return obj[k] ?? obj[lowerFirst] ?? null
      }
      const container = key ? getContainer(this.payloadEntry, key) : null
      const lista = container ? (container.ListaDebitos || container.listaDebitos || []) : []
      return Array.isArray(lista) ? lista : []
    },
    periodoApuracaoSelecionado() {
      const entry = this.payloadEntry
      return (entry && (entry.PeriodoApuracao || entry.periodoApuracao)) || ''
    },
    modoHistorico() {
      // Verifica se há filtros aplicados
      const temFiltroSituacao = this.filtroAtual?.filtroBy?.some(filtro => filtro.colunas && filtro.colunas.includes('situacaoApuracao'))
      return !this.isFiltroDataAplicada && !temFiltroSituacao
    },
  },
  async mounted() {
    this.$bvModal.show('debitos-mit')
    // Seleciona primeiro grupo por padrão (usa label para exibição)
    const [firstGrupo] = this.gruposExistentes
    this.grupoSelecionado = firstGrupo ? firstGrupo.label : ''
    this.carregando = true

    // Verifica se há filtros aplicados
    const temFiltroSituacao = this.filtroAtual?.filtroBy?.some(filtro => filtro.colunas && filtro.colunas.includes('situacaoApuracao'))
    const deveExibirTodosDebitos = !this.isFiltroDataAplicada && !temFiltroSituacao

    let filtroBody

    if (deveExibirTodosDebitos) {
      // Busca todos os débitos históricos da empresa
      filtroBody = {
        empresas: [this.empresaId],
        filtroBy: [
          {
            colunas: ['EmpresaId', 'empresaId'],
            valor: this.empresaId.toString(),
          },
        ],
        ordemBy: [
          {
            colunas: ['PeriodoApuracao', 'periodoApuracao'],
            ordem: 'desc',
          },
        ],
      }
    } else {
      // Com filtros aplicados, busca apenas a apuração específica
      filtroBody = {
        empresas: [this.empresaId],
        filtroBy: [
          {
            colunas: ['IdApuracao'],
            valor: this.idApuracao,
          },
        ],
        ordemBy: [],
      }
    }

    try {
      const { data } = await service.getDebitosValores(this.pagina, this.tamanhoPagina, filtroBody)
      this.dados = data || null

      const getValores = resp => {
        if (Array.isArray(resp?.Valores)) return resp.Valores
        if (Array.isArray(resp?.valores)) return resp.valores
        if (Array.isArray(resp?.dados)) return resp.dados
        if (resp?.dados && typeof resp.dados === 'object') return [resp.dados]
        return []
      }
      const valores = getValores(data)

      let pool = valores
      if (deveExibirTodosDebitos) {
        const empresasParaBusca = [this.empresaId]
        const byEmpresas = valores.filter(v => {
          const emp = Number(v.EmpresaId ?? v.empresaId)
          return !Number.isNaN(emp) && empresasParaBusca.includes(emp)
        })
        pool = byEmpresas.length ? byEmpresas : valores
      }

      let filteredPool
      if (deveExibirTodosDebitos) {
        // Sem filtros, usa todos os débitos da empresa
        filteredPool = pool
      } else {
        // Com filtros aplicados, filtra por apurações visíveis
        const visibleIds = await this.getVisibleApuracaoIds()
        filteredPool = this.isFiltroDataAplicada ? pool.filter(v => {
          const ap = Number(v.IdApuracao ?? v.idApuracao)
          return visibleIds.includes(ap)
        }) : pool
      }

      if (deveExibirTodosDebitos) {
        // Sem filtros, combina débitos de todos os períodos
        this.payloadEntry = this.combineDebitsFromAllEntries(filteredPool)
      } else if (this.isFiltroDataAplicada) {
        // Com filtro de data, usa entrada específica
        const entry = filteredPool.find(v => {
          const ap = Number(v.IdApuracao ?? v.idApuracao)
          return !Number.isNaN(ap) && ap === Number(this.idApuracao)
        }) || filteredPool[0] || null
        this.payloadEntry = entry
      } else {
        // Outros casos, combina todas as entradas
        this.payloadEntry = this.combineDebitsFromAllEntries(filteredPool)
      }

      // Filtra os grupos para exibir apenas os que possuem valor de débito (> 0)
      if (this.payloadEntry) {
        const getContainer = (obj, k) => {
          if (!k || !obj) return null
          const lowerFirst = k.charAt(0).toLowerCase() + k.slice(1)
          return obj[k] ?? obj[lowerFirst] ?? null
        }
        const temDebito = g => {
          const container = getContainer(this.payloadEntry, g.key)
          const lista = container ? (container.ListaDebitos || container.listaDebitos || []) : []
          if (!Array.isArray(lista) || !lista.length) return false
          // Considera débito válido quando existe algum item com valor numérico > 0
          return lista.some(d => {
            const raw = d?.ValorDebito ?? d?.valorDebito ?? d?.valor ?? 0
            const num = Number(raw)
            return Number.isFinite(num) && num > 0
          })
        }
        const gruposComDebito = this.gruposExistentes.filter(temDebito)
        this.gruposExistentes = gruposComDebito

        // Ajusta seleção para o primeiro grupo com débito (se necessário)
        if (!gruposComDebito.length) {
          this.grupoSelecionado = ''
        } else if (!gruposComDebito.some(g => (g.label || g.key) === this.grupoSelecionado)) {
          this.grupoSelecionado = gruposComDebito[0].label || gruposComDebito[0].key
        }
      }
    } catch (error) {
      this.dados = null
      this.payloadEntry = null
    } finally {
      this.carregando = false
    }
  },
  methods: {
    async getVisibleApuracaoIds() {
      // Usa os mesmos filtros do grid para buscar IDs de apuração visíveis
      const filtro = { ...this.filtroAtual }
      const { data } = await service.getPaginado(1, 10000, filtro)
      const getValores = resp => {
        if (Array.isArray(resp?.Valores)) return resp.Valores
        if (Array.isArray(resp?.valores)) return resp.valores
        if (Array.isArray(resp?.dados)) return resp.dados
        if (resp?.dados && typeof resp.dados === 'object') return [resp.dados]
        return []
      }
      const valores = getValores(data)
      return valores.map(v => Number(v.IdApuracao ?? v.idApuracao)).filter(id => !Number.isNaN(id))
    },
    async getAllEmpresaApuracaoIds(empresaId) {
      // Busca todos os IDs de apuração da empresa
      const filtroEmpresa = {
        filtroBy: [
          {
            colunas: ['EmpresaId', 'empresaId'],
            valor: empresaId.toString(),
          },
        ],
        ordemBy: [],
      }

      try {
        const { data } = await service.getPaginado(1, 10000, filtroEmpresa)
        const getValores = resp => {
          if (Array.isArray(resp?.Valores)) return resp.Valores
          if (Array.isArray(resp?.valores)) return resp.valores
          if (Array.isArray(resp?.dados)) return resp.dados
          if (resp?.dados && typeof resp.dados === 'object') return [resp.dados]
          return []
        }
        const valores = getValores(data)
        const ids = valores.map(v => Number(v.IdApuracao ?? v.idApuracao)).filter(id => !Number.isNaN(id))
        return ids
      } catch (error) {
        this.$erro('Erro ao buscar IDs de apuração da empresa:', error)
        return []
      }
    },
    fecharModal() {
      this.$emit('hidden')
    },
    selecionarGrupo(grupo) {
      this.grupoSelecionado = grupo
    },
    combineDebitsFromAllEntries(entries) {
      if (!Array.isArray(entries) || !entries.length) return null

      const combined = {}

      entries.forEach(entry => {
        const periodoApuracao = entry.PeriodoApuracao || entry.periodoApuracao || ''
        const empresaId = entry.EmpresaId || entry.empresaId || ''
        const razaoSocial = entry.RazaoSocial || entry.razaoSocial || ''
        const cnpj = entry.CadastroFederal || entry.cadastroFederal || ''

        const getContainer = (obj, k) => {
          if (!k || !obj) return null
          const lowerFirst = k.charAt(0).toLowerCase() + k.slice(1)
          return obj[k] ?? obj[lowerFirst] ?? null
        }

        this.gruposExistentes.forEach(grupo => {
          const container = getContainer(entry, grupo.key)
          const lista = container ? (container.ListaDebitos || container.listaDebitos || []) : []

          if (Array.isArray(lista) && lista.length) {
            if (!combined[grupo.key]) {
              combined[grupo.key] = { ListaDebitos: [] }
            }

            const debitsWithInfo = lista.map(debit => ({
              ...debit,
              periodoApuracao,
              empresaId,
              razaoSocial,
              cnpj,
            }))

            combined[grupo.key].ListaDebitos.push(...debitsWithInfo)
          }
        })
      })

      return combined
    },
  },
}
</script>
